import * as React from "react";
import { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { DateTime } from "luxon";
import { ApiLogDto } from "../../../Api/SocketRulesEngineApi.generated";
import { theme } from "../../global/styles/theme";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Chip from "@mui/material/Chip";
import {
  formatCurrency,
  formatNumber,
} from "../../../utilities/numberFormatUtility";

const RuleApplicationHistoryRow = ({
  logData,
  openRule,
}: {
  logData: ApiLogDto;
  openRule: any;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const [indexOfOriginalTotal, setIndexOfOriginalTotal] = useState<number>(0);
  const [indexOfModifiedTotal, setIndexOfModifiedTotal] = useState<number>(0);

  React.useEffect(() => {
    if (logData?.metadata?.additionalDataHeaders != null) {
      setIndexOfOriginalTotal(
        logData?.metadata?.additionalDataHeaders.indexOf("Original Total"),
      );
      setIndexOfModifiedTotal(
        logData?.metadata?.additionalDataHeaders.indexOf("Modified Total"),
      );
    }
  }, [logData]);

  function getColumnColor(value: string) {
    switch (value) {
      case "Carrier Code":
      case "Carrier":
      case "Route":
      case "Quote Number":
      case "Quote ID":
      case "Quote #":
        return theme.palette.primary.main;
      case "Original Total":
      case "Modified Total":
      case "Original Value":
      case "Modified Value":
      case "Markup Applied":
        return theme.palette.secondary.main;
      default:
        return theme.palette.primary.main;
    }
  }

  function getColumnAlignment(value: string) {
    switch (value) {
      case "Carrier Code":
      case "Carrier":
      case "Route":
        return "";
      case "Quote Number":
      case "Quote ID":
      case "Quote #":
      case "Original Total":
      case "Modified Total":
      case "Original Value":
      case "Modified Value":
      case "Markup Applied":
        return "right";
      default:
        return "";
    }
  }

  // Fixing formatting of names for display purposes and backwards compatability
  function getColumnDisplayName(value: string) {
    switch (value) {
      case "Quote Number":
        return "Quote #";
      case "Original Total":
        return "Original Value";
      case "Modified Total":
        return "Modified Value";
      default:
        return value;
    }
  }

  return (
    <React.Fragment>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset !important" } }}
        style={
          open
            ? { backgroundColor: "rgba(119, 123, 131, .1)" }
            : { backgroundColor: "white" }
        }
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} sx={{ cursor: "pointer" }}>
          {logData.ruleDetails?.ruleGroupName}
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} sx={{ cursor: "pointer" }}>
          {logData.conditionMatch}
        </TableCell>
        <TableCell onClick={() => setOpen(!open)} sx={{ cursor: "pointer" }}>
          {logData.ruleDetails?.modificationString}
        </TableCell>
        <TableCell
          onClick={() => setOpen(!open)}
          sx={{ cursor: "pointer", color: theme.palette.text.secondary }}
          width={250}
        >
          <Grid container sx={{ width: 160 }}>
            <Grid item xs={6}>
              {logData.requestDate?.toLocaleString(DateTime.DATE_SHORT)}
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              {logData.requestDate?.toLocaleString(DateTime.TIME_WITH_SECONDS)}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell width={50}>
          <IconButton onClick={() => openRule(logData)}>
            <ReadMoreIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={
            open
              ? {
                  paddingBottom: 0,
                  paddingTop: 0,
                  backgroundColor: "rgba(119, 123, 131, .1)",
                }
              : { paddingBottom: 0, paddingTop: 0 }
          }
          colSpan={6}
        >
          <Collapse
            in={open}
            timeout={{ appear: 100, enter: 10, exit: 0 }}
            unmountOnExit
          >
            <Grid container>
              <Grid item xs={2} />
              <Grid item xs={8}>
                <Box sx={{ margin: 1 }} style={{ backgroundColor: "white" }}>
                  <Typography variant="h6" sx={{ ml: 3 }}>
                    Quotes
                  </Typography>
                  <Table size="small" aria-label="purchases">
                    <TableHead>
                      {logData?.metadata?.additionalDataHeaders != null && (
                        <TableRow>
                          {logData?.metadata?.additionalDataHeaders.map(
                            (header, index) => {
                              return (
                                <TableCell
                                  key={`header_cell_` + index}
                                  sx={{
                                    color: getColumnColor(header),
                                    textAlign: getColumnAlignment(header),
                                  }}
                                >
                                  {getColumnDisplayName(header)}
                                </TableCell>
                              );
                            },
                          )}
                          <TableCell
                            align="right"
                            sx={{ color: theme.palette.secondary.main }}
                          >
                            Markup Applied
                          </TableCell>
                        </TableRow>
                      )}
                    </TableHead>
                    <TableBody>
                      {logData.metadata?.additionalDataRows != null
                        ? Object.entries(
                            logData.metadata?.additionalDataRows,
                          ).map((data) => {
                            const originalValue = parseFloat(
                              data[1][indexOfOriginalTotal],
                            );
                            const modifiedValue = parseFloat(
                              data[1][indexOfModifiedTotal],
                            );
                            const difference = modifiedValue - originalValue;
                            const percentChange =
                              originalValue !== 0
                                ? ((difference / originalValue) * 100).toFixed(
                                    2,
                                  )
                                : "0.00";
                            const sign = difference >= 0 ? "+" : "";
                            const color =
                              difference >= 0
                                ? theme.palette.error.main
                                : theme.palette.success.main;

                            return (
                              <TableRow key={data[0]}>
                                {data[1].map((value, index) => {
                                  const header =
                                    logData?.metadata?.additionalDataHeaders?.[
                                      index
                                    ];
                                  switch (header) {
                                    case "Route":
                                      return (
                                        <TableCell key={`data_cell_${index}`}>
                                          {value}
                                        </TableCell>
                                      );
                                    case "Carrier Code":
                                    case "Carrier":
                                      return (
                                        <TableCell key={`data_cell_${index}`}>
                                          <Chip
                                            sx={{
                                              backgroundColor:
                                                theme.palette.primary.main,
                                              color: "white",
                                            }}
                                            label={data[1][index]}
                                          />
                                        </TableCell>
                                      );
                                    case "Original Value":
                                    case "Original Total":
                                    case "Modified Value":
                                    case "Modified Total":
                                      return (
                                        <TableCell
                                          key={`data_cell_${index}`}
                                          align="right"
                                        >
                                          {formatCurrency(data[1][index])}
                                        </TableCell>
                                      );
                                    default:
                                      return (
                                        <TableCell
                                          key={`data_cell_${index}`}
                                          align="right"
                                        >
                                          {value}
                                        </TableCell>
                                      );
                                  }
                                })}
                                <TableCell align="right" sx={{ color }}>
                                  {sign}
                                  {formatCurrency(Math.abs(difference))} ({sign}
                                  {percentChange}%)
                                </TableCell>
                              </TableRow>
                            );
                          })
                        : null}
                    </TableBody>
                  </Table>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default RuleApplicationHistoryRow;
